import React, {useEffect, useState} from "react";
import {useTranslation} from "@unibuddy/intl";
import {Box, VisuallyHidden} from "@unibuddy/patron";
import styled from "styled-components";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {useMutation, useQuery} from "@apollo/client";

import {pageNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";

import sharedEmbeddedEvents from "../../../../../shared/EmbeddedEvents/constants/EmbeddedEvents";
import useAnalytics from "../../../../../shared/AnalyticsNew/hooks/useAnalytics";
import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import {PageTitle} from "../../../../../components/PageTitle/PageTitle";
import PageHeader from "../../../../../shared/Shared/components/PageHeader/PageHeader";
import {IntlNamespaces} from "../../../../../translations/types";
import DelinkedMessage from "../../components/DelinkedMessageBox/DelinkedMessage";
import {StyledChatComponent} from "../common/StyledChatComponent";
import checkAllowDelinkedForApplicant from "../../../../../shared/Chat/utils/checkAllowDelinkedForApplicant";
import {getMentorInboxInfoOne} from "../../utils/InboxInfo";
import {ChatUILegacy} from "../../components/ChatUI/ChatUILegacy";
import {PRESIGNUP_MESSAGE_KEY} from "../../../../../shared/constants";
import {getParameterByName} from "../../../../../shared/Utils/UrlUtils";
import Queue, {QueueItem} from "../../../../../shared/General/components/Queue/Queue";
import {PopupQuestions} from "../../components/PopupQuestions/PopupQuestions";
import {INTL_INBOX} from "../../../../../translations/common";
import {INTL_CONVERSATION_WITH} from "../../../../../translations/chat";
import {useEmbeddedEvents} from "../../hooks/useEmbeddedEvents";
import useAuth from "../../../../../shared/Auth/hooks/useAuth";
import {useHasUniversityAppliedStatus} from "../../hooks/useHasUniversityAppliedStatus";
import {rootURL} from "../../../../../shared/Utils/WhereAmI";
import chatGroupQuery from "../../../../../shared/Chat/queries/chatGroupQuery";
import mentorIntroMessageQuery from "../../../../../shared/Chat/queries/mentorIntroMessageQuery";
import {DOES_NOT_EXIST} from "../../../../../shared/Utils/ErrorConstantsUtils";
import EmptyState from "../../../../../shared/General/components/EmptyState/EmptyState";
import EmptySuggestionsIcon from "../../components/EmptySuggestionsIcon/EmptySuggestionsIcon";
import LoadingChat from "../../components/LoadingChat/LoadingChat";
import sendMessageMutation from "../../../../../shared/Chat/mutations/sendMessageMutation";
import translateIntroMessage from "../../../../../shared/Chat/utils/translateIntroMessage";
import {useGetOrCreateChatGroup} from "../../hooks/useGetOrCreateChatGroup";

const StyledContainer = styled(Box)`
    @media (min-width: 420px) {
        overflow: hidden;
    }
`;

export function StartChatLegacy() {
    const {opponentId} = useParams<{opponentId: string}>();
    const {t} = useTranslation([IntlNamespaces.CHAT, IntlNamespaces.INBOX]);
    const {trackEvent} = useAnalytics();
    const history = useHistory();
    const location = useLocation();
    const search = location.search ?? "";
    const [messages, setMessages] = useState([]);
    const {getOrCreateChatGroup, chatGroupData} = useGetOrCreateChatGroup();
    const [sendMessage] = useMutation(sendMessageMutation);
    const [pendingMessage, setPendingMessage] = useState<string | null>(null);
    const postEmbeddedMessage = useEmbeddedEvents();
    const hasUniversityAppliedStatus = useHasUniversityAppliedStatus();
    const {authState} = useAuth();
    const university = useUniversity();
    const {slug: universitySlug, features} = university;
    const showAppliedToUniversityQuestion = !features.get("hideAppliedToUniversityQuestion");
    const isDegreeTypesEnabled = features.get("enableDegreeTypes");
    const preSignUpMessage = window.localStorage.getItem(PRESIGNUP_MESSAGE_KEY(opponentId));
    let chatGroup = {opponent: {}, delinked: false};

    const {
        data: chatGroupQueryData,
        loading: chatGroupQueryLoading,
        error: chatGroupQueryError,
    } = useQuery(chatGroupQuery, {
        variables: {opponentId, universitySlug, isDegreeTypesEnabled},
        fetchPolicy: "cache-and-network",
        skip: !!preSignUpMessage,
    });

    const {
        data: mentorIntroMessageQueryData,
        loading: mentorIntroMessageQueryLoading,
        error: mentorIntroMessageQueryError,
    } = useQuery(mentorIntroMessageQuery, {
        variables: {mentorId: opponentId, universitySlug, isDegreeTypesEnabled},
        fetchPolicy: "cache-and-network",
    });

    const ambassadorName =
        (mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender
            ?.firstName as string) ?? "Buddy";

    const meAuthState = authState.me;
    const isDelinked = checkAllowDelinkedForApplicant(universitySlug, meAuthState);

    const queue = showAppliedToUniversityQuestion && (
        <Queue>
            <QueueItem enabled={!hasUniversityAppliedStatus}>
                {({isOpen, onDismiss}) => {
                    return <PopupQuestions onDismiss={onDismiss} isOpen={isOpen} />;
                }}
            </QueueItem>
        </Queue>
    );

    const handleSendMessage = async (chatGroupId: string, text: string) => {
        let ubSource: string | string[] = getParameterByName(location.search, "ub_source");
        ubSource = ubSource ? ubSource.split(" - ") : [];
        const buddyPosition = getParameterByName(location.search, "buddyPosition") || ubSource[0];
        const buddyGroupType = getParameterByName(location.search, "buddyGroupType") || ubSource[1];

        let opponentType =
            mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender?.accountRole ||
            buddyGroupType ||
            "mentor";

        if (
            opponentType === "mentor" &&
            mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender?.degreeLevel ===
                "Alumni"
        ) {
            opponentType = "alumni";
        }

        trackEvent(sharedEvents.conversationStarted.name, {
            buddyPosition,
            opponentId,
            opponentType,
        });

        try {
            await sendMessage({
                variables: {
                    text,
                    chatGroupId,
                },
            });

            trackEvent(sharedEvents.messageSent.name, {
                conversationId: chatGroupId,
                opponentId,
                opponentType,
                chatVersion: "Version 2",
            });
        } catch (error) {
            reportError(error);
        }

        try {
            postEmbeddedMessage(sharedEmbeddedEvents.messageSent.name, {});
        } catch (error) {
            console.error("Error sending embedded message:", error);
        }

        history.replace(`${rootURL(location.pathname)}/inbox/${chatGroupId}${search}`);
    };

    const onSendFirstMessage = ({text}: {text: string}) => {
        setPendingMessage(text);
        const newMessage = {
            id: Date.now(),
            created: new Date().toISOString(),
            text,
            sender: {
                id: authState.me.anyUser.id,
                name: "Me",
                profilePhoto: "",
            },
        };
        setMessages([...messages, newMessage]);
    };

    const redirectToRelink = () => {
        // redirecting to 2nd stage signup on settings tab
        history.replace(location.pathname, {from: "delinked"});
    };

    useEffect(() => {
        trackEvent(sharedEvents.startChatPageViewed.name, {
            prefix: pageNames.startChat,
            universitySlug,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(
        function attemptChatGroupCreationFromPendingMessage() {
            if (pendingMessage) {
                try {
                    getOrCreateChatGroup({
                        variables: {
                            opponentId,
                            universitySlug,
                            isDegreeTypesEnabled,
                        },
                    });
                } catch (error) {
                    console.error("Error executing getOrCreateChatGroup:", error);
                }
            }
        },
        [pendingMessage, getOrCreateChatGroup, opponentId, universitySlug, isDegreeTypesEnabled],
    );

    useEffect(
        function sendMessageWhenChatGroupExistsAndMessagePending() {
            if (chatGroupData?.getOrCreateChatGroup?.id && pendingMessage) {
                handleSendMessage(chatGroupData.getOrCreateChatGroup.id as string, pendingMessage);
                setPendingMessage(null);
            }
        },
        [chatGroupData, handleSendMessage, pendingMessage],
    );

    const chatGroupId: string | undefined = chatGroupQueryData?.chatGroup?.id;
    if (chatGroupId) {
        history.replace(`${rootURL(location.pathname)}/inbox/${chatGroupId}${search}`);
    }

    if (preSignUpMessage) {
        try {
            const parsedMessage = JSON.parse(preSignUpMessage);
            onSendFirstMessage({text: parsedMessage.text});
        } catch (e) {
            console.error("Error parsing preSignUpMessage", e);
        }
        window.localStorage.removeItem(`preSignUpMessage-${opponentId}`);
    }

    if (chatGroupQueryLoading || mentorIntroMessageQueryLoading) return <LoadingChat />;

    if (
        chatGroupQueryError?.graphQLErrors[0]?.extensions?.code === DOES_NOT_EXIST ||
        mentorIntroMessageQueryError?.graphQLErrors[0]?.extensions?.code === DOES_NOT_EXIST
    ) {
        return (
            <EmptyState
                title={t("profileNotFound", "Profile not found")}
                text={t(
                    "profileNotFoundDescription",
                    "Sorry, it appears that this profile is not available.",
                )}
                image={<EmptySuggestionsIcon />}
            />
        );
    }

    if (mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage && messages.length < 2) {
        chatGroup = {
            opponent: {
                ...mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender,
                inboxInfoOne:
                    getMentorInboxInfoOne({
                        sender:
                            mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender ??
                            {},
                        university,
                        t,
                    }) ||
                    mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender?.degree
                        ?.name ||
                    mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender
                        ?.staffFields?.department,
                inboxInfoTwo:
                    mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender
                        ?.university?.name,
                isEnterprise:
                    mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage?.sender
                        ?.university?.isEnterprise,
            },
            delinked: isDelinked,
        };
    }

    return (
        <div id="chat-page">
            <PageTitle
                title={t(
                    `${IntlNamespaces.CHAT}:${INTL_CONVERSATION_WITH.key}`,
                    `Conversation with ${ambassadorName}`,
                    {
                        name: ambassadorName,
                    },
                )}
            />
            <VisuallyHidden>
                <PageHeader
                    title={t(`${IntlNamespaces.INBOX}:${INTL_INBOX.key}`, INTL_INBOX.defaultValue)}
                />
            </VisuallyHidden>
            <StyledContainer>
                <StyledChatComponent>
                    <ChatUILegacy
                        messages={[
                            translateIntroMessage(
                                mentorIntroMessageQueryData?.mentorIntroMessage?.introMessage,
                                t,
                            ),
                            ...messages,
                        ]}
                        user={authState.me.anyUser}
                        chatGroup={chatGroup}
                        chatFooterHidden={isDelinked}
                        chatPopup={
                            isDelinked ? (
                                <DelinkedMessage redirectToRelink={redirectToRelink} />
                            ) : null
                        }
                        university={university}
                        onSend={onSendFirstMessage}
                        queue={queue}
                    />
                </StyledChatComponent>
            </StyledContainer>
        </div>
    );
}
